import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Landmark, Clock, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

const STATUS_STYLES = {
  approved:   { c: "text-[#8AE234] border-[#8AE234]/40 bg-[#8AE234]/10", l: "Active" },
  pending:    { c: "text-amber-300 border-amber-500/40 bg-amber-500/10", l: "Pending" },
  target_met: { c: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10", l: "Target Met · Released" },
  cleared:    { c: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10", l: "Cleared" },
  expired:    { c: "text-slate-400 border-slate-500/40 bg-slate-500/10", l: "Expired" },
  rejected:   { c: "text-red-300 border-red-500/40 bg-red-500/10", l: "Rejected" },
};

/**
 * Dashboard summary card for the user's Company Credit Line.
 * Feb-21: slab-aware. Reads /credit/config to build the empty-state pitch
 * dynamically from the current Admin-configured slab list.
 */
export default function CreditLineCard() {
  const [cl, setCl] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [my, c] = await Promise.all([
          api.get("/credit/my").then(r => r.data).catch(() => null),
          api.get("/credit/config").then(r => r.data).catch(() => null),
        ]);
        setCl(my?.credit_line || null);
        setCfg(c || null);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return null;

  if (!cl) {
    // Feb-21: slab-driven pitch. Falls back to legacy $500 – $5,000 phrasing if slabs empty.
    const slabs = cfg?.slabs || [];
    const min = slabs.length ? Math.min(...slabs.map(s => s.amount)) : (cfg?.min_amount || 500);
    const max = slabs.length ? Math.max(...slabs.map(s => s.amount)) : (cfg?.max_amount || 5000);
    return (
      <Link to="/credit" data-testid="credit-card-empty" className="nb-card p-5 block hover:border-[#EAB308]/40 transition-colors">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#EAB308] font-bold">
          <Landmark size={14}/> Company Credit Line
        </div>
        <div className="mt-2 font-heading text-white text-lg">Boost your capacity</div>
        <div className="text-slate-400 text-sm mt-1">
          Apply for a ${Number(min).toFixed(0)} – ${Number(max).toFixed(0)} credit line — {slabs.length ? `${slabs.length} slabs available. ` : ""}Instant activation via email OTP.
        </div>
        <div className="text-xs text-[#EAB308] mt-3 inline-flex items-center gap-1">Apply now <ArrowRight size={12}/></div>
      </Link>
    );
  }

  const style = STATUS_STYLES[cl.status] || STATUS_STYLES.pending;
  const expires = cl.expires_at ? new Date(cl.expires_at).getTime() : null;
  const daysLeft = expires ? Math.max(0, Math.ceil((expires - Date.now()) / (24 * 3600 * 1000))) : null;
  const mult = Math.round(cl.business_volume_multiplier || cfg?.business_multiplier || 10);

  return (
    <Link to="/credit" data-testid="credit-card-active" className="nb-card p-5 block hover:border-[#EAB308]/40 transition-colors">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#EAB308] font-bold">
          <Landmark size={14}/> Company Credit Line
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${style.c}`}>{style.l}</span>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
        <div>
          <div className="text-slate-500 text-[10px] uppercase tracking-widest">Credit</div>
          <div className="font-mono text-white">{fmt(cl.amount)}</div>
        </div>
        <div>
          <div className="text-slate-500 text-[10px] uppercase tracking-widest">{mult}× Business</div>
          <div className="font-mono text-[#EAB308]">{fmt(cl.business_volume_generated)} / {fmt(cl.business_volume_target)}</div>
        </div>
        <div>
          <div className="text-slate-500 text-[10px] uppercase tracking-widest">
            {cl.status === "target_met" || cl.status === "cleared" ? "Credit Profit (Released)" : "Hold Profit"}
          </div>
          <div className="font-mono text-cyan-300">{fmt((cl.status === "target_met" || cl.status === "cleared") ? cl.hold_profit_total_earned : cl.hold_profit)}</div>
        </div>
      </div>

      {(cl.status === "target_met" || cl.status === "cleared") && (
        <div className="mt-3 text-xs rounded-lg border border-cyan-500/30 bg-cyan-500/5 text-cyan-200 px-3 py-2">
          🎉 {mult}× target met — your ${Number(cl.hold_profit_released || cl.hold_profit_total_earned || 0).toFixed(2)} of credit profit has been released to your Real Balance. Future daily profits from credit-funded bots will land directly in your wallet.
        </div>
      )}

      {cl.status === "approved" && (
        <>
          <div className="mt-4">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500">
              <span>{mult}× Target Progress</span>
              <span className="font-mono text-white">{Number(cl.business_volume_progress_pct||0).toFixed(0)}%</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full bg-[#8AE234]" style={{width: `${Math.min(100, Number(cl.business_volume_progress_pct||0))}%`}}/>
            </div>
          </div>
          {daysLeft !== null && (
            <div className="text-xs text-slate-400 mt-3 flex items-center gap-1">
              <Clock size={12}/> {daysLeft > 0 ? <>Expires in <span className="text-white font-mono">{daysLeft} day{daysLeft===1?"":"s"}</span></> : "Expired today"}
            </div>
          )}
        </>
      )}
    </Link>
  );
}
